import type { Country } from '@sunwebgroup/uds-ui/fields/phone-field'
import type {
  BookingDetailsState,
  ParticipantEditFieldConfiguration,
  ParticipantEditFieldOption,
  ParticipantEditFormValues,
  ParticipantEditMode,
  ParticipantEditRule,
  ParticipantPhoneType,
} from '../../domain/types'
import { countryData } from '@sunwebgroup/uds-ui/fields/phone-field'
import { parseDate } from '@/scopes/account/common/utils/bookingDates'
import { isExternallyEditable } from '../../domain/participantEditRules'
import { toPhoneFormValues } from '../utils/participantEdit/participantEditPhoneFields'

const NOT_EDITABLE_HINT_KEYS: Record<string, string> = {
  HasExternalFlights: 'booking_details_participant_edit_not_editable_airline_hint',
  IsAfterLimitTimeBeforeDeparture: 'booking_details_participant_edit_not_editable_time_before_departure_hint',
  EditionNoAllowedForAgeRange: 'booking_details_participant_edit_not_editable_age_range_hint',
}

const DEFAULT_NOT_EDITABLE_HINT_KEY = 'booking_details_participant_edit_not_editable_default_hint'

export interface ParticipantEditFieldState {
  currentValue: string
  disabled: boolean
  required: boolean
  pattern: string | undefined
  patternsByCountry: Record<string, string>
  options: ParticipantEditFieldOption[]
  allowedValues: string[]
  minValue: string | undefined
  maxValue: string | undefined
  notEditableHintKey: string | undefined
}

export interface ParticipantEditPhoneNumbersFieldState {
  values: string[]
  disabled: boolean
  required: boolean
  countries: Country[]
  notEditableHintKey: string | undefined
}

export interface ParticipantEditFields {
  title: ParticipantEditFieldState | undefined
  gender: ParticipantEditFieldState | undefined
  firstName: ParticipantEditFieldState | undefined
  lastName: ParticipantEditFieldState | undefined
  dateOfBirth: ParticipantEditFieldState | undefined
  email: ParticipantEditFieldState | undefined
  street: ParticipantEditFieldState | undefined
  houseNumber: ParticipantEditFieldState | undefined
  houseNumberAddition: ParticipantEditFieldState | undefined
  postalCode: ParticipantEditFieldState | undefined
  city: ParticipantEditFieldState | undefined
  countryCode: ParticipantEditFieldState | undefined
  mobilePhoneNumbers: ParticipantEditPhoneNumbersFieldState | undefined
  emergencyPhoneNumbers: ParticipantEditPhoneNumbersFieldState | undefined
}

type ParticipantAddressSubField =
  | 'street'
  | 'houseNumber'
  | 'houseNumberAddition'
  | 'postalCode'
  | 'city'
  | 'countryCode'

interface ParticipantPhone {
  type?: string
  number?: string
}

function findConfiguration(rule: ParticipantEditRule | undefined, subType?: string): ParticipantEditFieldConfiguration | undefined {
  return rule?.configurations.find(configuration => configuration.subType === subType)
}

function findRule(state: BookingDetailsState, fieldName: string): ParticipantEditRule | undefined {
  return state.participantEdit.rules.find(rule => rule.fieldName === fieldName)
}

function belongsToForm(rule: ParticipantEditRule, mode: ParticipantEditMode): boolean {
  return mode === 'externalRequest' ? isExternallyEditable(rule) : true
}

export function hasParticipantEditExternalFields(state: BookingDetailsState): boolean {
  return state.participantEdit.rules.some(isExternallyEditable)
}

function isDisabledInForm(rule: ParticipantEditRule, mode: ParticipantEditMode): boolean {
  return mode === 'standard' && rule.editionType !== 'Editable'
}

function toFieldState(
  rule: ParticipantEditRule,
  configuration: ParticipantEditFieldConfiguration,
  currentValue: string,
  mode: ParticipantEditMode,
): ParticipantEditFieldState {
  const disabled = isDisabledInForm(rule, mode)

  return {
    currentValue,
    disabled,
    required: configuration.isMandatory,
    pattern: configuration.pattern,
    patternsByCountry: configuration.patternsByCountry,
    options: configuration.options,
    allowedValues: rule.allowedValues,
    minValue: rule.minValue,
    maxValue: rule.maxValue,
    notEditableHintKey: disabled ? getNotEditableHintKey(rule.reasons) : undefined,
  }
}

export function getParticipantEditField(
  state: BookingDetailsState,
  fieldName: string,
  mode: ParticipantEditMode = 'standard',
): ParticipantEditFieldState | undefined {
  const rule = findRule(state, fieldName)
  const configuration = findConfiguration(rule)

  if (!rule || !configuration || !belongsToForm(rule, mode)) {
    return undefined
  }

  return toFieldState(rule, configuration, String(rule.currentValue ?? ''), mode)
}

export function getParticipantEditPhones(
  state: BookingDetailsState,
  phoneType: ParticipantPhoneType,
  mode: ParticipantEditMode = 'standard',
): ParticipantEditPhoneNumbersFieldState | undefined {
  const rule = findRule(state, 'phones')
  const configuration = findConfiguration(rule, phoneType)

  if (!rule || !configuration || !belongsToForm(rule, mode)) {
    return undefined
  }

  const disabled = isDisabledInForm(rule, mode)
  const phones = Array.isArray(rule.currentValue) ? rule.currentValue as ParticipantPhone[] : []

  const numbers = getPhoneNumbersOfType(phones, phoneType)

  return {
    // A participant with no number of this type is still offered one empty field, as the legacy form does.
    values: numbers.length ? numbers : [''],
    disabled,
    required: configuration.isMandatory || isMainBookerMobile(state, phoneType),
    countries: configuration.phoneCountries.length ? configuration.phoneCountries : countryData,
    notEditableHintKey: disabled ? getNotEditableHintKey(rule.reasons) : undefined,
  }
}

export function getParticipantEditAddressField(
  state: BookingDetailsState,
  subField: ParticipantAddressSubField,
  mode: ParticipantEditMode = 'standard',
): ParticipantEditFieldState | undefined {
  const rule = findRule(state, 'address')
  const configuration = findConfiguration(rule, subField)

  if (!rule || !configuration || !belongsToForm(rule, mode)) {
    return undefined
  }

  const address = (rule.currentValue ?? {}) as Partial<Record<ParticipantAddressSubField, unknown>>

  return toFieldState(rule, configuration, String(address[subField] ?? ''), mode)
}

export function getParticipantEditFields(state: BookingDetailsState, mode: ParticipantEditMode): ParticipantEditFields {
  const field = (fieldName: string) => getParticipantEditField(state, fieldName, mode)
  const address = (subField: ParticipantAddressSubField) => getParticipantEditAddressField(state, subField, mode)
  const phones = (phoneType: ParticipantPhoneType) => getParticipantEditPhones(state, phoneType, mode)

  return {
    title: field('title'),
    gender: field('gender'),
    firstName: field('firstName'),
    lastName: field('lastName'),
    dateOfBirth: field('dateOfBirth'),
    email: field('email'),
    street: address('street'),
    houseNumber: address('houseNumber'),
    houseNumberAddition: address('houseNumberAddition'),
    postalCode: address('postalCode'),
    city: address('city'),
    countryCode: address('countryCode'),
    mobilePhoneNumbers: phones('Mobile'),
    emergencyPhoneNumbers: phones('Emergency'),
  }
}

export function getParticipantEditFormValues(
  state: BookingDetailsState,
  mode: ParticipantEditMode,
  defaultPhoneCountry: Country,
): ParticipantEditFormValues {
  const fields = getParticipantEditFields(state, mode)
  const value = (field: ParticipantEditFieldState | undefined) => field?.currentValue ?? ''
  const phoneValues = (field: ParticipantEditPhoneNumbersFieldState | undefined) =>
    toPhoneFormValues(field?.values ?? [], defaultPhoneCountry)

  return {
    title: value(fields.title),
    gender: value(fields.gender),
    firstName: value(fields.firstName),
    lastName: value(fields.lastName),
    dateOfBirth: parseDate(value(fields.dateOfBirth)) ?? undefined,
    email: value(fields.email),
    street: value(fields.street),
    houseNumber: value(fields.houseNumber),
    houseNumberAddition: value(fields.houseNumberAddition),
    postalCode: value(fields.postalCode),
    city: value(fields.city),
    countryCode: value(fields.countryCode),
    mobilePhoneNumbers: phoneValues(fields.mobilePhoneNumbers),
    emergencyPhoneNumbers: phoneValues(fields.emergencyPhoneNumbers),
  }
}

function isMainBookerMobile(state: BookingDetailsState, phoneType: ParticipantPhoneType): boolean {
  return phoneType === 'Mobile'
    && state.participants[state.participantEdit.participantId]?.type === 'MainBooker'
}

function isPhoneOfType(phone: ParticipantPhone, phoneType: ParticipantPhoneType): boolean {
  return (phone.type ?? '').toLowerCase() === phoneType.toLowerCase()
}

function isPhoneOfUnsupportedType(phone: ParticipantPhone): boolean {
  const phoneType = (phone.type ?? '').toLowerCase()
  return phoneType !== 'emergency' && phoneType !== 'mobile'
}

function getPhoneNumbersOfType(phones: ParticipantPhone[], phoneType: ParticipantPhoneType): string[] {
  // Unknown types fall back to the mobile field so they stay visible instead of disappearing.
  const matches = phoneType === 'Mobile'
    ? phones.filter(phone => isPhoneOfType(phone, phoneType) || isPhoneOfUnsupportedType(phone))
    : phones.filter(phone => isPhoneOfType(phone, phoneType))

  return matches.map(({ number }) => number ?? '')
}

function getNotEditableHintKey(reasons: string[]): string {
  const matchedKey = reasons.map(reason => NOT_EDITABLE_HINT_KEYS[reason]).find(Boolean)
  return matchedKey ?? DEFAULT_NOT_EDITABLE_HINT_KEY
}
